import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import PremiumDashboard from "@/components/boost/PremiumDashboard";
import { HERO } from "@/constants/content";
import { HOME } from "@/constants/testIds";
import { useReserve } from "@/components/boost/ReserveContext";

const Hero = () => {
  const { open } = useReserve();
  const scrollTo = (id) => () => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative overflow-hidden pt-32 sm:pt-36 lg:pt-40 pb-16 sm:pb-24">
      {/* Backgrounds */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(90% 60% at 50% 0%, rgba(16,185,129,0.10), transparent 60%), radial-gradient(60% 40% at 100% 20%, rgba(163,230,53,0.18), transparent 60%), linear-gradient(180deg, #ffffff, #f8fafc)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)",
        }}
      />

      <div className="container-x">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="chip"
            >
              <span className="live-dot" /> {HERO.chip}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.7 }}
              className="mt-5 font-display font-extrabold tracking-tight text-slate-900 leading-[0.95]"
              style={{ fontSize: "clamp(2.75rem, 7vw, 5.25rem)" }}
            >
              {HERO.headlineLines.map((l, i) => (
                <motion.span
                  key={l}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
                  className="block"
                >
                  {i === HERO.headlineLines.length - 1 ? (
                    <span className="gradient-text-emerald">{l}</span>
                  ) : (
                    l
                  )}
                </motion.span>
              ))}
              <motion.span
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="block text-slate-900/80 font-display font-bold"
                style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)", letterSpacing: "-0.02em" }}
              >
                {HERO.headlineSuffix}
              </motion.span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
            >
              {HERO.subheading}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button data-testid={HOME.heroReserve} onClick={open} className="btn-primary text-base">
                Reserve My Seat
                <ArrowUpRight className="w-4 h-4" />
              </button>
              <button
                data-testid={HOME.heroExplore}
                onClick={scrollTo("#roadmap")}
                className="btn-ghost text-base"
              >
                Explore Roadmap
              </button>

              <div className="flex items-center gap-2 pl-2">
                <div className="relative">
                  <div
                    className="px-3 py-1.5 rounded-full font-mono text-xs bg-slate-900 text-lime-300 border border-lime-400/30"
                    style={{ boxShadow: "0 6px 20px -6px rgba(163,230,53,0.5)" }}
                  >
                    ₹99 Only
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  <div>One-time registration</div>
                  <div>Limited seats</div>
                </div>
              </div>
            </motion.div>

            {/* Trust bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 font-mono uppercase tracking-widest"
            >
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Practical · Beginner-first
              </span>
              <span className="hidden sm:inline">·</span>
              <span>Shopify · Meta Ads · AI Tools</span>
              <span className="hidden sm:inline">·</span>
              <span>Private WhatsApp Community</span>
            </motion.div>
          </div>

          <div className="relative">
            <PremiumDashboard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
